import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
'../components/ui/accordion';
import {
  MapPin,
  Filter,
  Target,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';

const Landing = () => {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [expandedTestimonial, setExpandedTestimonial] = useState(null);

  React.useEffect(() => {
    document.title = 'Zeus Weather - Sales Operating System for Roofing Companies';
  }, []);

  const scrollToCTA = () => {
    document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200/20 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center space-x-2">
              <img
                src="https://customer-assets.emergentagent.com/job_roof-sales-hub/artifacts/8eyuijji_ChatGPT_Image_Jan_28__2026__08_23_42_PM-removebg-preview.png"
                alt="Zeus Weather"
                className="h-10 w-auto" />

            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost"
                onClick={() => window.open('https://blog.getzeusweather.com', '_blank')}
                className="text-slate-700 hover:text-slate-900"
              >
                Blog
              </Button>
              <Button 
                variant="ghost"
                onClick={() => window.open('https://portal.getzeusweather.com', '_blank')}
                className="text-slate-700 hover:text-slate-900"
              >
                Login
              </Button>
              <Button
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white transition-all duration-300 hover:scale-105">

                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        {/* Hail swath background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/hail-swath-bg.png)' }}
        ></div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-900/90"></div>
        
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-6 text-white tracking-tight leading-[1.1]">
              More Doors.<br />More Deals.
            </h1>
            <p className="text-xl sm:text-2xl text-blue-100 mb-8 leading-relaxed max-w-2xl mx-auto">
              Know exactly where to knock after every storm. Without changing how you already work.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-2xl">

                Start Your 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => window.open('https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977', '_blank')}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 transition-all duration-300">

                See Zeus in Action
              </Button>
            </div>

            <p className="text-sm text-blue-200 mt-6">Trusted by roofing contractors across the U.S.</p>
          </div>
        </div>
      </section>

      {/* Z.E.U.S. Identity Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">

            {/* Acrostic display */}
            <div className="mb-8">
              <div className="inline-block text-left">
                <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                  <span className="text-red-600">Z</span><span className="text-slate-900">ero</span>
                </p>
                <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                  <span className="text-red-600">E</span><span className="text-slate-900">xcuses,</span>
                </p>
                <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                  <span className="text-red-600">U</span><span className="text-slate-900">nlock</span>
                </p>
                <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                  <span className="text-red-600">S</span><span className="text-slate-900">ales</span>
                </p>
              </div>
            </div>

            {/* Supporting copy */}
            <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Storms don't wait. Neither should your sales team. Zeus shows exactly where to go — so your reps stop guessing and start closing.
            </p>
            <p className="text-base sm:text-lg text-slate-400 mt-4 max-w-xl mx-auto">
              Storm data alone doesn't close deals. Canvassing tools alone don't find opportunities. Zeus does both.
            </p>

          </div>
        </div>
      </section>

      {/* Works With Your CRM Section */}
      <section className="py-12 sm:py-16 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12 border-2 border-blue-200">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Complements Your CRM, Doesn't Replace It
                </h2>
                <p className="text-lg text-slate-700 max-w-3xl mx-auto">
                  Zeus handles opportunity discovery and field execution. 
                  Your CRM handles job management and production. Hand off qualified leads and let each system do what it does best.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Zeus Finds the Opportunity</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Finding opportunities (storm + retail)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Field execution & canvassing</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Activity tracking & visibility</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-blue-600 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Automated follow-ups</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Your CRM Tracks the Job</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="text-slate-400 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Job management & workflow</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-slate-400 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Estimates & proposals</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-slate-400 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Production scheduling</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="text-slate-400 mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Invoicing & payments</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-lg text-slate-700 font-medium">
                  ⚡ Zeus finds and qualifies. Your CRM closes and manages.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Product Visualization Section */}
      <section id="features" className="py-12 sm:py-20 bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                See Exactly Where to Send Your Team
              </h2>
              <p className="text-xl text-blue-100">
                Every door. Every detail. Right on their phone.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-4 bg-blue-500/20 rounded-2xl blur-xl"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_522c5977-b846-493f-b349-2811165f83cd/artifacts/zgokcjhw_mobile.png"
                    alt="Zeus Weather Mobile App"
                    className="relative rounded-lg shadow-2xl border-4 border-white/20 max-w-[300px] w-full h-auto" />
                </div>
              </div>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-blue-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Storm Impact Overlay</h3>
                    <p className="text-blue-100">
                      See exactly which neighborhoods were hit, with color-coded severity indicators showing hail size and wind damage.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Filter className="h-5 w-5 text-blue-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Smart Property Filters</h3>
                    <p className="text-blue-100">
                      Filter by home age, value, ownership type, roof condition, and more. Only see the properties worth your time.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Target className="h-5 w-5 text-blue-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Canvassing Mode</h3>
                    <p className="text-blue-100">
                      Switch to mobile view for field teams. Track visited properties, add notes, and prioritize your route on the fly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Real Results From Real Roofing Teams
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-5 sm:p-8">
                  <div className="mb-4">
                    <div className="flex text-yellow-500 mb-4">
                      {[...Array(5)].map((_, i) =>
                      <span key={i}>★</span>
                      )}
                    </div>
                  </div>
                  <p className={`text-slate-700 mb-6 italic ${expandedTestimonial !== 0 ? 'line-clamp-3 sm:line-clamp-none' : ''}`}>
                    "Joe and Team have taken Zeus to the next level allowing users of being able to pinpoint canvassing opportunities, the ability to send out direct marketing with a precision approach, and understand the weather/storm impact better than our competition. He and his team continue to refine the ap through user feedback. My team and I are very satisfied and can't wait to see future developments."
                  </p>
                  {expandedTestimonial !== 0 && (
                    <button
                      onClick={() => setExpandedTestimonial(0)}
                      className="text-blue-600 text-sm font-medium mb-4 sm:hidden"
                    >
                      Read more
                    </button>
                  )}
                  <div className="border-t pt-4">
                    <p className="font-bold text-slate-900">Steve Kneller</p>
                    <p className="text-sm text-slate-600">Storm Guard of Fort Worth North</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-slate-200">
                <CardContent className="p-5 sm:p-8">
                  <div className="mb-4">
                    <div className="flex text-yellow-500 mb-4">
                      {[...Array(5)].map((_, i) =>
                      <span key={i}>★</span>
                      )}
                    </div>
                  </div>
                  <p className={`text-slate-700 mb-6 italic ${expandedTestimonial !== 1 ? 'line-clamp-3 sm:line-clamp-none' : ''}`}>
                    "Zeus has made targeting our canvassing efforts very easy. The combination of storm and demographic data in one place, proactively alerting me when properties I'm most interested in have been affected by the weather is something no other weather service allows. I enjoy the simplicity of the platform. I just had to 'set it and forget it' when I told Zeus about the types of homes I'm interested in. After that, I get alerts and am on my way well before anyone else is aware."
                  </p>
                  {expandedTestimonial !== 1 && (
                    <button
                      onClick={() => setExpandedTestimonial(1)}
                      className="text-blue-600 text-sm font-medium mb-4 sm:hidden"
                    >
                      Read more
                    </button>
                  )}
                  <div className="border-t pt-4">
                    <p className="font-bold text-slate-900">Chris Parker</p>
                    <p className="text-sm text-slate-600">Storm Guard of Metro Detroit North</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-slate-200">
                <CardContent className="p-5 sm:p-8">
                  <div className="mb-4">
                    <div className="flex text-yellow-500 mb-4">
                      {[...Array(5)].map((_, i) =>
                      <span key={i}>★</span>
                      )}
                    </div>
                  </div>
                  <p className={`text-slate-700 mb-6 italic ${expandedTestimonial !== 2 ? 'line-clamp-3 sm:line-clamp-none' : ''}`}>
                    "The weather portion of the app dials into exact addresses and neighborhoods we have our eyes on. Quick address reports showing storm swath, wind speed and hail size. Automated filters set to notify wind speed and hail size within a specific watch zone. Manageable door knocking map for the sales team with integrated property and property owner information. Basically if Hail Recon and Spotio had a baby, this would be it……but better. Zeus weather app combines multiple tools the roofing industry utilizes in the field, all under one roof."
                  </p>
                  {expandedTestimonial !== 2 && (
                    <button
                      onClick={() => setExpandedTestimonial(2)}
                      className="text-blue-600 text-sm font-medium mb-4 sm:hidden"
                    >
                      Read more
                    </button>
                  )}
                  <div className="border-t pt-4">
                    <p className="font-bold text-slate-900">Brad Staples</p>
                    <p className="text-sm text-slate-600">AQUAGUARD MITIGATION</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Revenue Growth Proof */}
            <div className="mt-12 sm:mt-16">
              <img
                src="/images/revenue-growth-chart.png"
                alt="Zeus customers saw +540% increase in year-over-year revenue growth. Before Zeus: +2.85% avg return (2020-2022). After Zeus: +18.24% avg return (2023-2024)."
                className="w-full rounded-xl shadow-lg"
              />
              <p className="text-center text-sm text-slate-500 mt-4">
                Aggregate results from 32 roofing companies across 17 states.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                One Platform. One Price. One Job Covers 5 Years.
              </h2>
              <div className="inline-block bg-green-100 text-green-800 px-6 py-3 rounded-full font-semibold text-lg mb-8">
                🎉 Founders Pricing for the First 500 Customers
              </div>
              
              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-4 mb-12">
                <span className={`text-lg font-semibold ${selectedPlan === 'monthly' ? 'text-slate-900' : 'text-slate-500'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => setSelectedPlan(selectedPlan === 'monthly' ? 'annual' : 'monthly')}
                  className="relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  style={{ backgroundColor: selectedPlan === 'annual' ? '#2563eb' : '#cbd5e1' }}>

                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    selectedPlan === 'annual' ? 'translate-x-9' : 'translate-x-1'}`
                    } />

                </button>
                <span className={`text-lg font-semibold ${selectedPlan === 'annual' ? 'text-slate-900' : 'text-slate-500'}`}>
                  Annual
                </span>
                {selectedPlan === 'annual' &&
                <span className="ml-2 inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                    Save $300/year
                  </span>
                }
              </div>
            </div>
            
            {/* Single Pricing Card */}
            <Card className="border-4 border-blue-600 relative hover:shadow-xl transition-shadow duration-300 max-w-md mx-auto">
              <CardContent className="p-10">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">Zeus</h3>
                  <p className="text-base font-semibold text-blue-600 mb-4">
                    One extra job pays for Zeus for 5+ years.
                  </p>
                  <div className="mb-6">
                    {selectedPlan === 'monthly' ?
                    <>
                        <p className="text-5xl font-bold text-slate-900">
                          $150
                          <span className="text-xl text-slate-600">/month</span>
                        </p>
                        <p className="text-sm text-slate-500 mt-2">Billed monthly</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Replaces $500–$1,500/month in disconnected tools
                        </p>
                      </> :

                    <>
                        <p className="text-5xl font-bold text-slate-900">
                          $1,500
                          <span className="text-xl text-slate-600">/year</span>
                        </p>
                        <p className="text-sm text-slate-500 mt-2">
                          $125/month • Save $300 annually
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Replaces $500–$1,500/month in disconnected tools
                        </p>
                      </>
                    }
                  </div>
                  <p className="text-sm text-slate-600">
                    Cancel anytime. No contracts.
                  </p>
                </div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Unlimited users</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Unlimited storm tracking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Unlimited lead generation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Mobile app for iOS & Android</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Real-time storm alerts</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Advanced property filters</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Direct mail integration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="text-blue-600 mr-3 h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Priority support</span>
                  </li>
                </ul>
                
                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6"
                  onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}>

                  Start Your 30-Day Free Trial
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Does Zeus replace my CRM like JobNimbus or AccuLynx?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  <strong>No.</strong> Zeus is designed to work alongside your existing CRM, not replace it. Zeus handles opportunity discovery, field execution, and lead qualification—the front-end of your sales process. Your CRM handles job management, estimating, production scheduling, and invoicing—the back-end. Think of Zeus as the system that feeds better opportunities into your CRM, so your sales team can focus on closing deals instead of chasing bad leads.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Can Zeus help with retail roofing, or is it only for storm work?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  <strong>Zeus supports both storm and retail work.</strong> While storm tracking is a core feature, Zeus also lets you target retail opportunities by filtering homes based on age, roof condition, property value, and demographics. You can build targeted lists for neighborhoods with aging roofs or past customers who may need replacements—keeping your pipeline full year-round, not just after storms.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How accurate is the storm data?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Zeus uses NOAA radar data, National Weather Service reports, and verified damage reports to create highly accurate storm impact maps. We track hail size down to quarter-inch increments and wind speeds in real-time. Our data is refreshed every 15 minutes during active storm events.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Is this better than buying lead lists?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  <strong>Absolutely.</strong> Lead lists are static, outdated, and sold to multiple contractors—sometimes dozens. By the time you get them, the best properties have already been contacted. Zeus generates fresh, storm-specific leads only when damage actually occurs, and the data is yours alone. You're not competing with 20 other roofers for the same list. Plus, you're not paying per-lead markup—just one flat monthly fee for unlimited leads.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How fast can I start after a storm?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  <strong>Within hours.</strong> Storm data appears in Zeus 2–4 hours after an event. Property filtering and lead generation happen instantly after that. Many of our customers have crews in the field within 6–8 hours of a major storm—well before competitors even know where it hit or what damage occurred.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Does this work in my area?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Zeus covers all 50 states and tracks every significant weather event that could cause roof damage—hail, wind, tornadoes, hurricanes, and ice storms. If storms happen in your service area, Zeus works for you.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Can I cancel anytime?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. There are no long-term contracts or cancellation fees. You can cancel your subscription at any time from your account settings. We want you to stay because Zeus delivers results, not because you're locked in.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-8" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Do you offer training for my team?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. Every new account includes a live onboarding session where we walk you and your team through the platform. We also provide video tutorials, help documentation, and ongoing support.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta-section" className="py-12 sm:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-5xl font-bold mb-6">
              Zero Excuses.<br />
              <span className="text-blue-300">Unlock Your Sales.</span>
            </h2>
            <p className="text-xl text-blue-100 mb-4">
              Start your free trial and see why roofing teams across the country trust Zeus.
            </p>
            <p className="text-sm text-blue-300 mb-10">
              Storm season doesn't wait for you to figure out your tools.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button
                size="lg"
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-2xl">

                Start Your 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => window.open('https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977', '_blank')}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 transition-all duration-300">

                See Zeus in Action
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_zeus-roofer-app/artifacts/7vhqji80_logo1-removebg-preview%20%281%29.png"
                alt="Zeus Weather"
                className="h-10 w-auto mb-4" />

              <p className="text-sm text-slate-400">
                Sales Operating System for Roofing Companies
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><a href="https://blog.getzeusweather.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Support</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2025 Zeus Weather. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Landing;